import { Fragment, useEffect, useMemo, useState } from 'react';
import EarningWithdrawal from '../components/EarningWithdrawal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchDistributorDashboard,
  fetchDistributorRechargeDetails,
  fetchDistributorRegisterDetails,
  fetchMemberPointLogs,
  fetchMemberReferralOverview,
  type DistributorDashboard,
  type DistributorRechargeDetail,
  type DistributorRegisterDetail,
  type MemberPointLog,
  type MemberReferralOverview,
} from '@/api/member';
import { ensureMemberAccessToken, readMemberSession, refreshCurrentMember, type MemberSession } from '@/utils/memberSession';

interface EarningsRecord {
  id: string;
  date: string;
  type: string;
  user: string;
  amount: number;
  status: 'pending' | 'settled';
}

interface ReferralTransaction {
  id: string;
  date: string;
  amount: number;
  earned: number;
  type: string;
}

interface ReferralRecord {
  id: string;
  username: string;
  registerDate: string;
  totalRecharged: number;
  earnedCoins: number;
  transactions: ReferralTransaction[];
}

const dateText = (value?: string) => value ? value.replace('T', ' ').slice(0, 16) : '-';

function formatDateInput(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultReferralDateRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return { start: formatDateInput(start), end: formatDateInput(end) };
}

function referralRange(start: string, end: string) {
  return {
    startTime: start ? `${start} 00:00:00` : undefined,
    endTime: end ? `${end} 23:59:59` : undefined,
  };
}

const initialReferralDateRange = defaultReferralDateRange();

function getAvatarColor(name: string): string {
  const colors = ['#f59e0b', '#f472b6', '#a78bfa', '#34d399', '#fb7185', '#22d3ee', '#f97316', '#e879f9'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function getAvatarLetter(name: string): string {
  return name.charAt(0).toUpperCase();
}

function CommissionCircle({ rate }: { rate: number }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (rate / 100) * circumference;

  return (
    <div className="relative w-24 h-24 flex items-center justify-center flex-shrink-0">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="currentColor" strokeWidth="7" className="text-background-800" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-accent-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-foreground-50 font-heading">{rate}%</span>
      </div>
    </div>
  );
}

export default function CinemaReferralPage() {
  const { t } = useTranslation('cinema');
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberSession>(() => readMemberSession());
  const [dashboard, setDashboard] = useState<DistributorDashboard | null>(null);
  const [overview, setOverview] = useState<MemberReferralOverview | null>(null);
  const [registers, setRegisters] = useState<DistributorRegisterDetail[]>([]);
  const [recharges, setRecharges] = useState<DistributorRechargeDetail[]>([]);
  const [rewards, setRewards] = useState<MemberPointLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'earnings' | 'invites'>('earnings');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [earningsFilter, setEarningsFilter] = useState<'all' | 'pending' | 'settled'>('all');
  const [startDate, setStartDate] = useState(initialReferralDateRange.start);
  const [endDate, setEndDate] = useState(initialReferralDateRange.end);

  useEffect(() => {
    if (!member.isLoggedIn) {
      navigate('/login');
      return;
    }
    let alive = true;
    setLoading(true);
    setError('');
    void refreshCurrentMember()
      .then(async (next) => {
        if (!alive) return;
        setMember(next);
        const token = await ensureMemberAccessToken();
        if (!token) throw new Error('unauthorized');
        const initialRange = referralRange(initialReferralDateRange.start, initialReferralDateRange.end);
        const nextOverview = await fetchMemberReferralOverview(token, initialRange);
        if (!alive) return;
        setOverview(nextOverview);
        if (next.isDealer) {
          const [nextDashboard, registerResult, rechargeResult] = await Promise.all([
            fetchDistributorDashboard(token, initialRange),
            fetchDistributorRegisterDetails(token, { ...initialRange, pageNum: 1, pageSize: 100 }),
            fetchDistributorRechargeDetails(token, { ...initialRange, pageNum: 1, pageSize: 100 }),
          ]);
          if (!alive) return;
          setDashboard(nextDashboard);
          setRegisters(registerResult.list || []);
          setRecharges(rechargeResult.list || []);
        } else {
          const logs = await fetchMemberPointLogs(token, 1, 100);
          if (!alive) return;
          setRegisters(nextOverview.recentInvites || []);
          setRewards((logs.list || []).filter((item) => /invite|share|邀请|分享/i.test(`${item.bizType || ''} ${item.remark || ''}`)));
        }
      })
      .catch(() => alive && setError(t('cinema_referralLoadFailed')))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [member.isLoggedIn, navigate, t]);

  const inviteCode = member.shareCode || '-';
  const inviteLink = useMemo(() => {
    if (!member.shareCode || typeof window === 'undefined') return '';
    return member.shareUrl || `${window.location.origin}/?ref=${encodeURIComponent(member.shareCode)}`;
  }, [member.shareCode, member.shareUrl]);
  const profile = dashboard?.profile;
  const referralStats = {
    totalEarnings: (overview?.totalCommission ?? profile?.totalCommission ?? 0) / 100,
    withdrawable: (overview?.availableBalance ?? profile?.availableBalance ?? 0) / 100,
    frozen: (overview?.pendingWithdrawal ?? profile?.pendingWithdrawAmount ?? 0) / 100,
    invitedCount: overview?.registerCount || registers.length,
  };
  const conversionDaily = (overview?.dailySummaries || []).map((item) => ({
    date: item.date.slice(0, 10),
    browseUsers: item.browseUserCount,
    browseCount: item.browseCount,
    register: item.registerCount,
    rechargeUsers: item.rechargeUserCount,
    rechargeOrders: item.rechargeOrderCount,
    paidAmount: item.rechargeAmount / 100,
    commission: item.commissionAmount / 100,
    registerRate: `${item.browseUserCount ? ((item.registerCount / item.browseUserCount) * 100).toFixed(2) : '0.00'}%`,
    rechargeRate: `${item.browseUserCount ? ((item.rechargeUserCount / item.browseUserCount) * 100).toFixed(2) : '0.00'}%`,
  }));
  const conversionTotals = conversionDaily.reduce((total, item) => ({
    pv: total.pv + item.browseCount,
    register: total.register + item.register,
    rechargeUsers: total.rechargeUsers + item.rechargeUsers,
    paidAmount: total.paidAmount + item.paidAmount,
    commission: total.commission + item.commission,
  }), { pv: 0, register: 0, rechargeUsers: 0, paidAmount: 0, commission: 0 });
  const conversionSummary = {
    ...conversionTotals,
    rechargeRate: `${conversionTotals.register ? ((conversionTotals.rechargeUsers / conversionTotals.register) * 100).toFixed(2) : '0.00'}%`,
  };
  const commissionRate = {
    currentRate: (overview?.firstLevelRatioBp ?? profile?.firstLevelRatioBp ?? 0) / 100,
    baseRate: (overview?.baseFirstLevelRatioBp ?? profile?.baseFirstLevelRatioBp ?? 0) / 100,
  };
  const earningsRecords = useMemo<EarningsRecord[]>(() => {
    if (member.isDealer) return recharges.map((item) => ({
      id: String(item.id),
      date: dateText(item.paidAt || item.createdAt),
      type: item.level === 2 ? t('cinema_secondLevelRate') : t('cinema_firstLevelRate'),
      user: item.inviteeNickName || item.inviteeUserName || `#${item.inviteeUserId}`,
      amount: item.commissionAmount / 100,
      status: item.status === 'paid' ? 'settled' : 'pending',
    }));
    return rewards.map((item) => ({
      id: String(item.id),
      date: dateText(item.createdAt),
      type: (item.remark || t('cinema_inviteRewards')).replaceAll('积分', '金币'),
      user: member.name,
      amount: item.changeAmount,
      status: 'settled',
    }));
  }, [member.isDealer, member.name, recharges, rewards, t]);
  const recentReferrals = useMemo<ReferralRecord[]>(() => registers.map((registration) => {
    const transactions = recharges.filter((item) => item.inviteeUserId === registration.inviteeUserId);
    return {
      id: String(registration.id),
      username: registration.inviteeNickName || registration.inviteeUserName || `#${registration.inviteeUserId}`,
      registerDate: dateText(registration.createdAt),
      totalRecharged: transactions.reduce((sum, item) => sum + item.rechargeAmount / 100, 0),
      earnedCoins: transactions.reduce((sum, item) => sum + item.commissionAmount / 100, 0),
      transactions: transactions.map((item) => ({
        id: String(item.id),
        date: dateText(item.paidAt || item.createdAt),
        amount: item.rechargeAmount / 100,
        earned: item.commissionAmount / 100,
        type: item.level === 2 ? t('cinema_secondLevelRate') : t('cinema_firstLevelRate'),
      })),
    };
  }), [recharges, registers, t]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const toggleExpand = (id: string) => setExpandedId(expandedId === id ? null : id);

  const handleDateQuery = async () => {
    if (startDate && endDate && startDate > endDate) {
      setError(t('cinema_dateRangeError'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) throw new Error('unauthorized');
      const range = referralRange(startDate, endDate);
      if (member.isDealer) {
        const [nextOverview, nextDashboard, registerResult, rechargeResult] = await Promise.all([
          fetchMemberReferralOverview(token, range),
          fetchDistributorDashboard(token, range),
          fetchDistributorRegisterDetails(token, { ...range, pageNum: 1, pageSize: 100 }),
          fetchDistributorRechargeDetails(token, { ...range, pageNum: 1, pageSize: 100 }),
        ]);
        setOverview(nextOverview);
        setDashboard(nextDashboard);
        setRegisters(registerResult.list || []);
        setRecharges(rechargeResult.list || []);
      } else {
        setOverview(await fetchMemberReferralOverview(token, range));
      }
    } catch {
      setError(t('cinema_referralLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const filteredEarnings = useMemo(() => {
    if (earningsFilter === 'all') return earningsRecords;
    return earningsRecords.filter((r) => r.status === earningsFilter);
  }, [earningsFilter, earningsRecords]);

  const formatNumber = (n: number) => n.toLocaleString('zh-CN');
  const formatMoney = (n: number) => `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="pt-14 md:pt-16 pb-20 md:pb-24">
      <div className="max-w-3xl mx-auto px-4 md:px-10 lg:px-14 space-y-5">
        {/* Page header */}
        <div className="py-4 md:py-6">
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-2xl font-bold font-heading text-foreground-50">{t('cinema_referral')}</h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-background-950 bg-[#FB923C] whitespace-nowrap">
              {t('cinema_vipLabel')}
            </span>
          </div>
          <p className="text-xs md:text-sm text-foreground-500 mt-1.5">{t('cinema_referralDesc')}</p>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-background-800/40 bg-background-900/50 py-3 text-xs text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-accent-400"></i>
            {t('cinema_loading')}
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300">{error}</div>
        )}

        {/* ====== Invite Link Card ====== */}
        <div className="relative overflow-hidden rounded-2xl border border-background-800/60 bg-background-900 shadow-xl">

          <div className="relative p-5 md:p-7">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-500/20 text-accent-400">
                <i className="ri-link text-sm"></i>
              </span>
              <span className="text-sm font-semibold text-white">{t('cinema_referralLinkTitle')}</span>
            </div>
            <p className="text-xs text-white/60 mb-4">{t('cinema_referralLinkDesc')}</p>

            <div className="flex flex-col sm:flex-row gap-2.5 mb-2.5">
              <div className="flex-1 bg-black/50 rounded-lg px-4 py-3 text-sm text-white/70 border border-white/10 truncate">
                {inviteLink}
              </div>
              <button
                onClick={() => handleCopy(inviteLink)}
                className="flex items-center justify-center gap-1.5 px-5 py-3 rounded-lg bg-accent-500 text-background-950 text-sm font-semibold hover:bg-accent-500/90 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-file-copy-line text-xs"></i>
                </span>
                {copied === inviteLink ? t('cinema_copied') : t('cinema_copyLink')}
              </button>
            </div>

            <div className="flex items-center gap-3 bg-black/40 rounded-lg px-4 py-2.5 border border-white/10">
              <span className="text-xs text-white/60">{t('cinema_inviteCodeLabel')}</span>
              <span className="text-sm font-bold tracking-[0.2em] text-accent-400 flex-1">{inviteCode}</span>
              <button
                onClick={() => handleCopy(inviteCode)}
                className="text-xs font-semibold text-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                {copied === inviteCode ? t('cinema_copied') : t('cinema_copy')}
              </button>
            </div>
          </div>
        </div>

        <section
          aria-labelledby="referral-rules-title"
          className="rounded-lg border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3.5 md:px-5"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-amber-300">
              <i className="ri-information-line text-base"></i>
            </span>
            <div className="min-w-0">
              <h2 id="referral-rules-title" className="text-sm font-bold text-foreground-100">
                {t('cinema_referralRulesTitle')}
              </h2>
              <div className="mt-1.5 space-y-1 text-xs leading-5 text-foreground-400 md:text-sm md:leading-6">
                <p>{t('cinema_referralRulesCoins')}</p>
                <p>{t('cinema_referralRulesCommission')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ====== Stats Cards ====== */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: t('cinema_totalEarnings'), value: formatMoney(referralStats.totalEarnings), unit: '', color: 'text-foreground-50', icon: 'ri-bar-chart-box-line', iconBg: 'bg-background-800 text-foreground-500' },
            { label: t('cinema_withdrawable'), value: formatMoney(referralStats.withdrawable), unit: '', color: 'text-accent-400', icon: 'ri-wallet-3-line', iconBg: 'bg-accent-500/15 text-accent-400' },
            { label: t('cinema_frozen'), value: formatMoney(referralStats.frozen), unit: '', color: 'text-amber-400', icon: 'ri-time-line', iconBg: 'bg-amber-500/15 text-amber-400' },
            { label: t('cinema_invitedCount'), value: formatNumber(referralStats.invitedCount), unit: t('cinema_people'), color: 'text-foreground-50', icon: 'ri-user-add-line', iconBg: 'bg-background-800 text-foreground-500' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-900/60 rounded-xl p-4 border border-background-800/40">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-foreground-500">{stat.label}</span>
                <span className={`w-7 h-7 flex items-center justify-center rounded-lg ${stat.iconBg}`}>
                  <i className={`${stat.icon} text-sm`}></i>
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className={`text-xl font-bold ${stat.color}`}>{stat.value}</span>
                <span className="text-xs text-foreground-500">{stat.unit}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ====== Conversion Stats ====== */}
        <EarningWithdrawal source="distribution" />
        <div className="rounded-2xl bg-background-900/50 border border-background-800/40 overflow-hidden">
          <div className="p-4 md:p-5">
            <div className="mb-4">
              <h2 className="text-sm font-bold text-foreground-50">{t('cinema_conversionStats')}</h2>
              <p className="text-[11px] text-foreground-500 mt-0.5">{t('cinema_conversionStatsDesc')}</p>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
              <label className="flex min-w-0 items-center gap-3 rounded-lg border border-background-800/50 bg-background-950/60 px-3 py-2">
                <span className="shrink-0 text-xs text-foreground-500">{t('cinema_startDate')}</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  aria-label={t('cinema_startDate')}
                  max={endDate || undefined}
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground-200 outline-none"
                />
              </label>
              <label className="flex min-w-0 items-center gap-3 rounded-lg border border-background-800/50 bg-background-950/60 px-3 py-2">
                <span className="shrink-0 text-xs text-foreground-500">{t('cinema_endDate')}</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  aria-label={t('cinema_endDate')}
                  min={startDate || undefined}
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground-200 outline-none"
                />
              </label>
                <button
                  type="button"
                  onClick={handleDateQuery}
                  disabled={loading}
                  className="flex min-h-10 items-center justify-center gap-1 rounded-lg bg-accent-500 px-4 text-xs font-semibold text-background-950 disabled:opacity-50"
                >
                  <i className="ri-search-line"></i>
                  <span>{t('cinema_query')}</span>
                </button>
            </div>

            {/* Summary mini cards */}
            <p className="mb-2 text-[11px] text-foreground-500">{t(startDate || endDate ? 'cinema_selectedDatesTotal' : 'cinema_allDatesTotal')}</p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
              {[
                { label: '浏览', value: conversionSummary.pv },
                { label: '注册', value: conversionSummary.register },
                { label: '充值', value: conversionSummary.rechargeUsers },
                { label: '实付', value: formatMoney(conversionSummary.paidAmount) },
                { label: '分成', value: formatMoney(conversionSummary.commission), color: 'text-accent-400' },
                { label: '充值率', value: conversionSummary.rechargeRate },
              ].map((item) => (
                <div key={item.label} className="bg-background-950/40 rounded-lg p-3 border border-background-800/30">
                  <p className="text-[10px] text-foreground-500 mb-1">{item.label}</p>
                  <p className={`text-sm font-bold ${item.color || 'text-foreground-50'}`}>{item.value}</p>
                </div>
              ))}
            </div>

            {/* Daily table */}
            <div className="overflow-x-auto -mx-4 md:-mx-5 px-4 md:px-5">
              <table className="w-full min-w-[1120px]">
                <thead>
                  <tr className="border-b border-background-800/40">
                    <th className="text-left py-2.5 px-2 text-[10px] font-medium text-foreground-500 whitespace-nowrap">{t('cinema_colDate')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colPv')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colBrowseCount')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colRegister')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colRechargeUsers')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colRechargeOrders')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colPaidAmount')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colCommission')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colRegisterRate')}</th>
                    <th className="text-right py-2.5 px-2 text-[10px] font-medium text-foreground-500">{t('cinema_colRechargeRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionDaily.map((row, idx) => (
                    <tr key={row.date} className={`border-b border-background-800/20 ${idx % 2 === 0 ? 'bg-background-950/20' : ''}`}>
                      <td className="py-2.5 px-2 text-xs text-foreground-300 font-medium whitespace-nowrap">{row.date}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{row.browseUsers}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{row.browseCount}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{row.register}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{row.rechargeUsers}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{row.rechargeOrders}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{formatMoney(row.paidAmount)}</td>
                      <td className="py-2.5 px-2 text-xs text-accent-400 text-right font-semibold">{formatMoney(row.commission)}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{row.registerRate}</td>
                      <td className="py-2.5 px-2 text-xs text-foreground-400 text-right">{row.rechargeRate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ====== Commission Rate ====== */}
        <div className="rounded-2xl bg-background-900/50 border border-background-800/40 p-4 md:p-5 flex items-center gap-5">
          <CommissionCircle rate={commissionRate.currentRate} />
          <div className="flex-1 min-w-0 space-y-2.5">
            <h2 className="text-sm font-bold text-foreground-50">{t('cinema_commissionRateTitle')}</h2>
            <p className="text-xs text-foreground-500">{t('cinema_currentRateLabel')}</p>
            <div className="flex items-center justify-between py-1.5 border-b border-background-800/20">
              <span className="text-xs text-foreground-500">{t('cinema_baseRate')}</span>
              <span className="text-xs font-semibold text-foreground-300">{commissionRate.baseRate}%</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-b border-background-800/20">
              <span className="text-xs text-foreground-500">{t('cinema_currentRate')}</span>
              <span className="text-xs font-semibold text-accent-400">{commissionRate.currentRate}%</span>
            </div>
          </div>
        </div>

        {/* ====== Tabs: Earnings & Invites ====== */}
        <div className="rounded-2xl bg-background-900/50 border border-background-800/40 overflow-hidden">
          <div className="flex border-b border-background-800/50">
            <button
              onClick={() => setActiveTab('earnings')}
              className={`flex-1 py-3.5 text-sm font-semibold text-center transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'earnings'
                  ? 'text-accent-400 border-b-2 border-accent-400'
                  : 'text-foreground-500 hover:text-foreground-300'
              }`}
            >
              {t('cinema_earningsRecord')}
            </button>
            <button
              onClick={() => setActiveTab('invites')}
              className={`flex-1 py-3.5 text-sm font-semibold text-center transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'invites'
                  ? 'text-accent-400 border-b-2 border-accent-400'
                  : 'text-foreground-500 hover:text-foreground-300'
              }`}
            >
              {t('cinema_inviteDetail')}
            </button>
          </div>

          {activeTab === 'earnings' ? (
            <div className="p-4 md:p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-foreground-300">{t('cinema_allEarnings')}</h3>
                <div className="flex items-center gap-1.5">
                  {(['all', 'pending', 'settled'] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => setEarningsFilter(f)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        earningsFilter === f
                          ? 'bg-accent-500/15 text-accent-400 border border-accent-500/30'
                          : 'bg-background-950/60 text-foreground-500 border border-background-800/30 hover:text-foreground-300'
                      }`}
                    >
                      {f === 'all' && t('cinema_filterAll')}
                      {f === 'pending' && t('cinema_filterPending')}
                      {f === 'settled' && t('cinema_filterSettled')}
                    </button>
                  ))}
                </div>
              </div>

              {filteredEarnings.length > 0 ? (
                <>
                  <div className="divide-y divide-background-800/30 md:hidden">
                    {filteredEarnings.map((record) => (
                      <div key={record.id} className="flex items-center gap-3 py-3">
                        <div
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-background-50"
                          style={{ backgroundColor: getAvatarColor(record.user) }}
                        >
                          {getAvatarLetter(record.user)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-xs font-medium text-foreground-200">{record.user}</span>
                            <span className="rounded-full bg-accent-500/15 px-1.5 py-0.5 text-[9px] font-medium text-accent-400">{record.type}</span>
                          </div>
                          <p className="mt-1 truncate text-[10px] text-foreground-500">{record.date}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-accent-400">+{member.isDealer ? formatMoney(record.amount) : `${formatNumber(record.amount)} ${t('cinema_coinUnit')}`}</p>
                          <p className={`mt-1 text-[9px] ${record.status === 'settled' ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {record.status === 'settled' ? t('cinema_statusSettled') : t('cinema_statusPending')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[560px]">
                    <thead>
                      <tr className="border-b border-background-800/30">
                        <th className="text-left py-2.5 px-3 text-[10px] font-medium text-foreground-500">{t('cinema_earningsDate')}</th>
                        <th className="text-left py-2.5 px-3 text-[10px] font-medium text-foreground-500">{t('cinema_earningsType')}</th>
                        <th className="text-left py-2.5 px-3 text-[10px] font-medium text-foreground-500">{t('cinema_earningsUser')}</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-medium text-foreground-500">{t('cinema_earningsAmount')}</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-medium text-foreground-500">{t('cinema_earningsStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEarnings.map((record, idx) => (
                        <tr key={record.id} className={`border-b border-background-800/15 ${idx % 2 === 0 ? 'bg-background-950/20' : ''}`}>
                          <td className="py-2.5 px-3 text-xs text-foreground-500 whitespace-nowrap">{record.date}</td>
                          <td className="py-2.5 px-3">
                            <span className="inline-block text-[10px] font-medium bg-accent-500/15 text-accent-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                              {record.type}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-xs text-foreground-300">{record.user}</td>
                          <td className="py-2.5 px-3 text-xs text-accent-400 text-right font-semibold">+{member.isDealer ? formatMoney(record.amount) : `${formatNumber(record.amount)} ${t('cinema_coinUnit')}`}</td>
                          <td className="py-2.5 px-3 text-right">
                            <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                              record.status === 'settled'
                                ? 'bg-emerald-500/15 text-emerald-400'
                                : 'bg-amber-500/15 text-amber-400'
                            }`}>
                              {record.status === 'settled' ? t('cinema_statusSettled') : t('cinema_statusPending')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-800 text-foreground-600 mb-3">
                    <i className="ri-mail-close-line text-xl"></i>
                  </span>
                  <p className="text-sm text-foreground-500">{t('cinema_noEarningsRecords')}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b border-background-800/30">
                    <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">{t('cinema_referralUser')}</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500 whitespace-nowrap">{t('cinema_referralRegDate')}</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-foreground-500 whitespace-nowrap">{t('cinema_referralRecharged')}</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-foreground-500 whitespace-nowrap">{t('cinema_referralEarned')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReferrals.map((ref) => (
                    <Fragment key={ref.id}>
                      <tr
                        onClick={() => toggleExpand(ref.id)}
                        className="border-b border-background-800/20 hover:bg-background-800/30 transition-colors cursor-pointer"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2.5">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-background-50 flex-shrink-0"
                              style={{ backgroundColor: getAvatarColor(ref.username) }}
                            >
                              {getAvatarLetter(ref.username)}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-foreground-200">{ref.username}</span>
                              <span className={`w-4 h-4 flex items-center justify-center transition-transform ${expandedId === ref.id ? 'rotate-180' : ''}`}>
                                <i className="ri-arrow-down-s-line text-xs text-foreground-500"></i>
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-foreground-500 whitespace-nowrap">{ref.registerDate}</td>
                        <td className="py-3 px-4 text-sm text-foreground-300 text-right font-medium whitespace-nowrap">{formatMoney(ref.totalRecharged)}</td>
                        <td className="py-3 px-4 text-sm text-accent-400 text-right font-semibold whitespace-nowrap">+{formatMoney(ref.earnedCoins)}</td>
                      </tr>
                      {expandedId === ref.id && (
                        <tr>
                          <td colSpan={4} className="p-0">
                            <div className="bg-background-950/50 border-t border-background-800/20 px-6 py-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="w-4 h-4 flex items-center justify-center text-accent-400">
                                  <i className="ri-file-list-3-line text-sm"></i>
                                </span>
                                <span className="text-xs font-semibold text-foreground-300">
                                  {ref.username} 充值明细
                                </span>
                                <span className="text-[10px] text-foreground-500 bg-background-900/60 px-2 py-0.5 rounded-full">
                                  {ref.transactions.length}
                                </span>
                              </div>
                              <div className="overflow-hidden rounded-lg border border-background-800/30">
                                <table className="w-full">
                                  <thead>
                                    <tr className="bg-background-900/60">
                                      <th className="text-left py-2 px-3 text-[10px] font-medium text-foreground-500 whitespace-nowrap">时间</th>
                                      <th className="text-left py-2 px-3 text-[10px] font-medium text-foreground-500 whitespace-nowrap">类型</th>
                                      <th className="text-right py-2 px-3 text-[10px] font-medium text-foreground-500 whitespace-nowrap">充值金额</th>
                                      <th className="text-right py-2 px-3 text-[10px] font-medium text-foreground-500 whitespace-nowrap">我的收益</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ref.transactions.map((tx, tidx) => (
                                      <tr key={tx.id} className={`border-t border-background-800/20 ${tidx % 2 === 0 ? 'bg-background-950/30' : ''}`}>
                                        <td className="py-2 px-3 text-xs text-foreground-500 whitespace-nowrap">{tx.date}</td>
                                        <td className="py-2 px-3">
                                          <span className="inline-block text-[10px] font-medium bg-accent-500/15 text-accent-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                                            {tx.type}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 text-xs text-foreground-300 text-right font-medium">{formatMoney(tx.amount)}</td>
                                        <td className="py-2 px-3 text-xs text-accent-400 text-right font-semibold">+{formatMoney(tx.earned)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="flex justify-end mt-2 gap-4">
                                <span className="text-[10px] text-foreground-500">
                                  累计充值: <span className="text-foreground-300 font-medium">{formatMoney(ref.totalRecharged)}</span>
                                </span>
                                <span className="text-[10px] text-foreground-500">
                                  累计收益: <span className="text-accent-400 font-medium">+{formatMoney(ref.earnedCoins)}</span>
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
